/**
 * Shared delta chip (E2.1 design spec 4.2, round-3 fix 11.2): a coloured
 * percent-of-previous chip PLUS the comparison-period context text next to
 * it ("vs Jun 1-14") and a Tooltip explaining the coloring rule. Used by
 * both StatementCard (the financial-statement rows) and RevenueCard (the
 * 1NL revenue card) so the "green = better for 1NL" explanation lives in
 * one place.
 */
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { palette } from '../../theme.ts';
import { type DeltaMetricKey, computeDelta } from './dashboardHelpers.ts';

const TOOLTIP_SUFFIX = 'Green = better for 1NL (a lower payout keeps more).';

export interface DeltaChipProps {
  metric: DeltaMetricKey;
  current: number | null;
  previous: number | null;
  /** The comparison label (E2.1 spec 11.2), e.g. "vs Jun 1-14" -- rendered
   *  beside the chip. */
  contextLabel: string;
  /** ComparisonContext.tooltipText -- the tooltip's lead sentence. */
  tooltipText: string;
  /** An optional card-specific sentence between the lead and the colouring
   *  rule. */
  tooltipNote?: string;
}

export function DeltaChip({
  metric,
  current,
  previous,
  contextLabel,
  tooltipText,
  tooltipNote,
}: Readonly<DeltaChipProps>): React.JSX.Element | null {
  if (current === null) return null;
  const delta = computeDelta(metric, current, previous);
  if (delta === null) return null;
  // Filled triangles (owner ruling 2026-07-15): line arrows read as
  // navigation; the ticker-style solid triangle is the financial idiom.
  // The drop-up/down glyphs sit small in their 24px viewbox, so bump the
  // rendered size to keep the same visual weight the arrows had.
  const Icon = delta.direction === 'up' ? ArrowDropUpIcon : ArrowDropDownIcon;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.75 }}>
      <Tooltip title={[tooltipText, tooltipNote, TOOLTIP_SUFFIX].filter(Boolean).join(' ')}>
        <Chip
          size="small"
          icon={<Icon sx={{ fontSize: '1.4rem', ml: '-4px' }} />}
          label={`${delta.pct >= 0 ? '+' : ''}${delta.pct.toFixed(1)}%`}
          color={delta.favorable ? 'success' : 'error'}
          variant="outlined"
          sx={{ height: 22, fontSize: '0.72rem', fontWeight: 700 }}
        />
      </Tooltip>
      <Typography variant="caption" sx={{ color: palette.textSecondary }}>
        {contextLabel}
      </Typography>
    </Box>
  );
}

export default DeltaChip;
